import { RequestParams } from 'idt-ecommerce-api/_api/api-util/api-request.models';
import { CategoriesHelpers } from 'idt-ecommerce-api/catalog/categories/categories.helpers';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { StoreDetail, StoreSummary } from 'idt-ecommerce-api/stores/stores.models';


export class UrlsWebHelpers {

  /**
   * Obtiene la url de una categoría
   * @param context Contexto de ejecución de ecommerce
   * @param categoryId Identificador de la categoría
   * @param categorySectionPath Path de secciones de la categoría (pe.novedades/ultimas-novedades).
   *                            Si no se le pasa se intenta obtener buscando la categoría en el contexto.
   * @param catalogId Identificador del catálogo
   */
  public static getCategoryUrl(
    context: EcommerceContext,
    categoryId: string,
    categorySectionPath?: string,
    catalogId?: string
  ): string {
    let url = this.buildBaseUrl(context);

    /* page regex */
    url += context.seo.urlTemplates.category;

    if (!categorySectionPath && context.categories) { // search category in context
      const category = CategoriesHelpers.category(context.categories, categoryId);
      categorySectionPath = category ? category.sectionPath : '';
    }

    /* replace regex placeholders */
    url = UrlsWebHelpers.replacePlaceholders(
      url,
      context.store.countryCode.toLowerCase(),
      this.langForUrl(context),
      categorySectionPath ? (`${categorySectionPath}-`) : '',
      `c${categoryId}`,
      undefined,
      undefined,
      catalogId ? (`-${catalogId}`) : ''
    );
    url = UrlsWebHelpers.appendHtmlExtensionIfNeeded(url);
    return url;
  }

  /**
   * Obtiene la url de una categoría con placeholders para que puedan ser interpolados
   * @param context Contexto de ejecución de ecommerce
   * @param categoryId Identificador de la categoría
   * @param catalogId Identificador del catálogo
   */
  public static getCategoryUrlWithPlaceholders(context: EcommerceContext, categoryId: string): string {
    let url = `https://{{env.storeHost}}${context.seo.urlTemplates.category}`;

    url = UrlsWebHelpers.replacePlaceholders(
      url,
      '{{env.countryCodeLowerCase}}',
      '{{env.languageCode}}/',
      undefined,
      `c${categoryId}`
    );
    return UrlsWebHelpers.appendHtmlExtensionIfNeeded(url);
  }

  /**
   * Obtiene la url de una categoría para CMS
   * @param context Contexto de ejecución de ecommerce
   * @param categoryId Identificador de la categoría
   * @param categorySectionPath Path de secciones de la categoría (pe.novedades/ultimas-novedades).
   *                            Si no se le pasa se intenta obtener buscando la categoría en el contexto.
   * @param catalogId Identificador del catálogo
   */
  public static getCategoryCMSUrl(
    context: EcommerceContext,
    categoryId: string,
    categorySectionPath?: string,
    catalogId?: string
  ): string {
    /* page regex */
    let url = context.seo.urlTemplates.category;

    if (!categorySectionPath && context.categories) { // search category in context
      const category = CategoriesHelpers.category(context.categories, categoryId);
      categorySectionPath = category ? category.sectionPath : '';
    }

    /* replace regex placeholders */
    url = UrlsWebHelpers.replacePlaceholders(
      url,
      context.store.countryCode.toLowerCase(),
      this.langForUrl(context),
      categorySectionPath ? (`${categorySectionPath}-`) : '',
      `c${categoryId}`,
      undefined,
      undefined,
      catalogId ? (`-${catalogId}`) : ''
    );

    url = UrlsWebHelpers.appendHtmlExtensionIfNeeded(url);
    return url;
  }

  /**
   * Gets the SEO URL of a category from the storefront menu.
   *
   * If `isCanonicalReference` is `true` (or not provided), generates the canonical URL:
   *   `{baseUrl}/{countryCode}/{lang?}/{keyword}-n{seoId}`
   *
   * If `isCanonicalReference` is `false`, appends `?celement={categoryId}`:
   *   `{baseUrl}/{countryCode}/{lang?}/{keyword}-n{seoId}?celement={categoryId}`
   *
   * @param context Ecommerce execution context
   * @param keyword SEO text of the category (e.g. `woman/clothing/pants/cargo`)
   * @param seoId SEO identifier (e.g. `2357`)
   * @param categoryId Category identifier. Required when not a canonical reference
   * @param isCanonicalReference Indicates whether the URL is the canonical reference for the category
   */
  public static getStorefrontCategorySeoUrl(
    context: EcommerceContext,
    keyword: string,
    seoId: string,
    categoryId?: string,
    isCanonicalReference?: boolean
  ): string {
    const baseUrl = this.buildBaseUrl(context);
    const countryCode = context.store.countryCode.toLowerCase();
    const lang = this.langForUrl(context);

    let url = `${baseUrl}/${countryCode}/${lang}${keyword}-n${seoId}`;

    // if (isCanonicalReference === false && categoryId) {
    //   url += `?celement=${categoryId}`;
    // }

    return url;
  }

  /**
   * @deprecated Use {@link UrlsWebHelpers.getStorefrontCategorySeoUrl} instead.
   * Will be removed when ITX Rest API is fully decommissioned.
   *
   * Obtiene la url de una categoría para SEO
   * @param context Contexto de ejecución de ecommerce
   * @param categoryUrlParam Identificador de la categoría. Utilizado para SEO.
   * @param categoryUrl Texto categoria para url
   */
  public static getCategorySeoUrl(
    context: EcommerceContext,
    categoryUrlParam?: string,
    categoryUrl?: string
  ): string | undefined {
    if (!categoryUrl) {
      return undefined;
    }

    let url = this.buildBaseUrl(context);

    /* page regex */
    url += context.seo.urlTemplates.category;

    url = UrlsWebHelpers.replacePlaceholders(
      url,
      context.store.countryCode.toLowerCase(),
      this.langForUrl(context),
      categoryUrl ?? '',
      categoryUrlParam ? `?celement=${categoryUrlParam}` : ''
    );
    url = url.replace('.html', ''); // remove.html

    return url;
  }

  public static getProductUrl(
    context: EcommerceContext,
    productId: string,
    categoryId?: string,
    catalogId?: string,
    initColor?: string,
    unifiedUrl?: string,
    unifiedUrlParams?: string
  ): string {
    let url = this.buildBaseUrl(context);
    url += context.seo.urlTemplates.product;

    if (unifiedUrl === undefined) {
      const category = categoryId && context.categories
        ? CategoriesHelpers.category(context.categories, categoryId)
        : undefined;

      url = UrlsWebHelpers.replacePlaceholders(
        url,
        context.store.countryCode.toLowerCase(),
        this.langForUrl(context),
        category ? (`${category.sectionPath}-`) : '',
        `c${category ? category.id : '0'}`,
        productId,
        initColor ? (`c${initColor}`) : '',
        catalogId ? (`-${catalogId}`) : ''
      );
    } else {
      url = UrlsWebHelpers.replacePlaceholders(
        url,
        context.store.countryCode.toLowerCase(),
        this.langForUrl(context)
      );
      url = url.replace('p.html', '');
      url = `${url}${unifiedUrl}`;
      if (unifiedUrlParams !== undefined) {
        url = `${url}?pelement=${unifiedUrlParams}`;
      }
    }

    return url;
  }

  public static getProductUrlWithPlaceholders(
    context: EcommerceContext,
    productId: string,
    categoryId?: string
  ): string {
    const category = categoryId && context.categories
      ? CategoriesHelpers.category(context.categories, categoryId)
      : undefined;
    let url = `https://{{env.storeHost}}${context.seo.urlTemplates.product}`;

    url = UrlsWebHelpers.replacePlaceholders(
      url,
      '{{env.storeId}}',
      '{{env.languageId}}/',
      undefined,
      `c${category ? category.id : '0'}`,
      productId
    );
    return UrlsWebHelpers.appendHtmlExtensionIfNeeded(url);
  }

  /**
   * Recupera url del CMS para navegación por SPA
   * @param context contexto
   * @param productId Id producto
   */
  public static getProductUrlCMS(
    context: EcommerceContext,
    productId: string,
    productUrl?: string,
    categoryId?: string,
    catalogId?: string,
    initColor?: string
  ): string {
    const store = context.store.countryCode.toLowerCase();
    const lang = this.langForUrl(context);

    let url = context.seo.urlTemplates.product;
    const flattenedCategories = CategoriesHelpers.flatCategoriesMap(context.categories);
    const category = categoryId && flattenedCategories ? flattenedCategories[categoryId] : undefined;

    url = UrlsWebHelpers.replacePlaceholders(
      url,
      store,
      lang,
      category ? (`${category.sectionPath}-`) : undefined,
      `c${categoryId ?? '0'}`,
      productId,
      initColor ? (`c${initColor}`) : '',
      catalogId ? (`-${catalogId}`) : ''
    );
    url = UrlsWebHelpers.appendHtmlExtensionIfNeeded(url);
    return url;
  }

  public static addParamsToUrl(url: string, params: RequestParams): string {
    const marker = url.includes('?') ? '&' : '?';
    url += `${marker}${params.map((i) => `${encodeURIComponent(i.key)}=${encodeURIComponent(i.value)}`).join('&')}`;
    return url;
  }

  /**
   * @param store detalle de la tienda que contiene la url base de los estáticos
   * @param path ruta del estático
   */
  public static buildStaticUrl(store: StoreDetail, path: string | undefined): string | undefined {
    let url;
    if (path && store.details.staticBaseUrl) {
      url = `${store.details.staticBaseUrl}${path}`;
    }
    return url;
  }

  /**
   * Recibe el query param shareUrl y la tienda a la que se desea navegar
   * @param {string} shareUrl - The shareUrl query param retrieved from the URL
   * @param {StoreSummary} store - Info of the current store
   */
  public static getShareUrlForStore(shareUrl: string, store: StoreSummary): string | undefined {
    let newUrl: string | undefined;
    if (shareUrl && store?.countryCode) {
      const url = new URL(shareUrl);
      const regexToMatchCountry = /^\/\w{2}\//g;
      url.pathname = url.pathname.replace(regexToMatchCountry, `/${store.countryCode.toLowerCase()}/`);
      // dont use the origin (domain) to make it work with all environments
      newUrl = url.pathname + url.search;
    }
    return newUrl;
  }

  /**
   * Obtiene la url de una página
   * @param context Contexto de ejecución de ecommerce
   * @param slug Identificador de slug de página
   */
  public static getPageUrl(
    context: EcommerceContext,
    slug: string
  ): string {
    let url = this.buildBaseUrl(context);

    /* page regex */
    url += context.seo.urlTemplates.category;

    /* replace regex placeholders */
    url = UrlsWebHelpers.replacePlaceholders(
      url,
      context.store.countryCode.toLowerCase(),
      this.langForUrl(context),
      '',
      slug
    );
    url = UrlsWebHelpers.appendHtmlExtensionIfNeeded(url);
    return url;
  }

  /**
   * Obtiene la url de una página con placeholders para que puedan ser interpolados
   * @param context Contexto de ejecución de ecommerce
   * @param categoryId Identificador de slug de página
   */
  public static getPageUrlWithPlaceholders(context: EcommerceContext, slug: string): string {
    let url = `https://{{env.storeHost}}${context.seo.urlTemplates.category}`;

    url = UrlsWebHelpers.replacePlaceholders(
      url,
      '{{env.countryCodeLowerCase}}',
      '{{env.languageCode}}/',
      undefined,
      slug
    );
    return UrlsWebHelpers.appendHtmlExtensionIfNeeded(url);
  }

  // ---- Private functions ----

  private static buildBaseUrl(context: EcommerceContext) {
    const baseUrl = `https://${context.store.hostname}`;
    return baseUrl;
  }

  private static langForUrl(context: EcommerceContext): string {
    let langCode: string | undefined;

    if (context.currentLanguage.id !== context.store.storeDefaultLanguageId) {
      langCode = context.seo.languageUrlCodes[context.currentLanguage.id];
    }
    return langCode ? (`${langCode}/`) : '';
  }

  private static appendHtmlExtensionIfNeeded(url: string): string {
    const extension = '.html';
    return url.endsWith(extension) ? url : `${url}${extension}`;
  }

  /**
   * Replace a url with placeholders with custom strings
   * @param url String. Url with placeholders to be replaced
   * @param storeId String to use to replace \'{storeId}\' placeholder
   * @param languageId String to use to replace \'[{langId}/]\' placeholder
   * @param keyword String to use to replace \'[{keyWord}-]\' placeholder
   * @param categoryId String to use to replace \'c{categoryId}\' placeholder
   * @param productId String to use to replace \'{productId}\' placeholder
   * @param initColor String to use to replace \'[c{initColor}]\' placeholder
   * @param catalogId String to use to replace \'[-{catalogId}]\' placeholder
   */
  private static replacePlaceholders(
    url: string,
    storeId: string,
    languageId: string,
    keyword?: string,
    categoryId?: string,
    productId?: string,
    initColor?: string,
    catalogId?: string
  ): string {
    url = url.replace('{storeId}', storeId);
    url = url.replace('[{langId}/]', languageId);
    url = url.replace('[{keyWord}-]', keyword ?? '');
    url = url.replace('c{categoryId}', categoryId ?? '');
    url = url.replace('{productId}', productId ?? '');
    url = url.replace('[c{initColor}]', initColor ?? '');
    url = url.replace('[-{catalogId}]', catalogId ?? '');
    url = UrlsWebHelpers.appendHtmlExtensionIfNeeded(url);
    return url;
  }

}
